// Analysis Screen - Enhanced UX with Rotating Insights & Expert Diagnostics
import { useState, useEffect, useCallback, useRef } from 'react';
import { useApp } from '../hooks/useApp';
import { SCREENS } from '../config/constants';
import { storage } from '../services/storage';
import { getAppPassword, MODEL_LIST } from '../config/api.config';
import Logger from '../services/logger';
import { analyzeWithAI, generateMockResults } from '../services/openrouter';
import { Container, Text, Button, Card } from '../components/ui';
import Icons from '../components/Icons';
import { TCM_INSIGHTS, TECHNICAL_STEPS } from '../config/tcmInsights';

function AnalysisScreen() {
    const { setCurrentScreen, setAnalysisResults, saveToHistory, questionnaireAnswers, capturedImages, analysisType, showToast, setAnalysisData } = useApp();
    const [currentStep, setCurrentStep] = useState(0);
    const [error, setError] = useState(null);
    const [errorType, setErrorType] = useState(null);
    const [statusMessage, setStatusMessage] = useState('Đang khởi tạo...');
    const [isRetrying, setIsRetrying] = useState(false);
    const [selectedRetryModel, setSelectedRetryModel] = useState(() => storage.getSelectedModel());

    // Rotating Insights State
    const [insightIndex, setInsightIndex] = useState(0);
    const insightTimerRef = useRef(null);

    // Xoay vòng insight mỗi 4 giây
    useEffect(() => {
        insightTimerRef.current = setInterval(() => {
            setInsightIndex(prev => (prev + 1) % TCM_INSIGHTS.length);
        }, 4000);
        return () => clearInterval(insightTimerRef.current);
    }, []);

    const runAnalysis = useCallback(async () => {
        Logger.info('ANALYSIS', 'Starting analysis', { analysisType, imageCount: Object.keys(capturedImages).length });
        setError(null);
        setErrorType(null);

        try {
            // Mapping steps to Technical steps for more granular feel
            setCurrentStep(0);
            setStatusMessage('Số hóa dữ liệu & Tiền xử lý...');
            await new Promise(r => setTimeout(r, 1200));

            setCurrentStep(1);
            setStatusMessage('Phân tích Thiệt tượng (Màu & Rêu)...');
            await new Promise(r => setTimeout(r, 1500));

            const appPassword = getAppPassword();
            const resultsPromise = appPassword
                ? analyzeWithAI(capturedImages, questionnaireAnswers, analysisType)
                : (async () => {
                    await new Promise(r => setTimeout(r, 3000));
                    return generateMockResults(questionnaireAnswers);
                })();

            setCurrentStep(2);
            setStatusMessage('Diện chẩn & Ngũ sắc vùng mặt...');
            await new Promise(r => setTimeout(r, 1800));

            setCurrentStep(3);
            setStatusMessage('So khớp Bát Cương Biện Chứng...');

            // Wait for results
            const results = await resultsPromise;

            setCurrentStep(4);
            setStatusMessage('Đánh giá 18 hạng mục rủi ro...');
            await new Promise(r => setTimeout(r, 1500));

            setCurrentStep(5);
            setStatusMessage('Hoàn tất báo cáo!');
            await new Promise(r => setTimeout(r, 800));

            // Logic sau khi có kết quả
            if (!appPassword) {
                showToast('Chế độ demo: Kết quả mẫu', 'warning');
            }

            const needsVerification = results.suspected_patterns &&
                results.suspected_patterns.some(p => p.confidence >= 0.3 && p.confidence <= 0.85);

            if (needsVerification) {
                setAnalysisData({ preliminary_analysis: results, capturedImages });
                setCurrentScreen(SCREENS.SMART_QUESTIONNAIRE);
            } else {
                setAnalysisResults(results);
                saveToHistory(results);
                setCurrentScreen(SCREENS.RESULTS);
            }

        } catch (err) {
            Logger.error('ANALYSIS', 'Analysis failed', { error: err.message });
            // Handle error logic (giữ nguyên logic cũ)
            if (err.message.includes('401') || err.message.includes('Mật khẩu')) {
                setError('Phiên đăng nhập hết hạn');
                setErrorType('AUTH');
            } else {
                setError(err.message);
                setErrorType('UNKNOWN');
            }
        }
    }, [capturedImages, questionnaireAnswers, analysisType]);

    useEffect(() => {
        runAnalysis();
    }, []);

    const handleRetry = async () => {
        setIsRetrying(true);
        if (selectedRetryModel) storage.setSelectedModel(selectedRetryModel);
        await runAnalysis();
        setIsRetrying(false);
    };

    if (error) {
        return (
            <Container className="min-h-screen bg-theme-bg flex flex-col items-center justify-center p-6" safe>
                <div className="w-full max-w-sm text-center">
                    <div className="w-20 h-20 bg-danger/10 text-danger rounded-full flex items-center justify-center mx-auto mb-6">
                        <Icons.Warning />
                    </div>
                    <Text variant="h2" className="mb-2">Có lỗi xảy ra</Text>
                    <Text variant="caption" className="mb-6">{error}</Text>

                    <Card className="mb-6 text-left">
                        <Text className="font-semibold text-sm mb-2 block">💡 Cách khắc phục:</Text>
                        <Text variant="small" className="text-theme-secondary italic">
                            Thử đổi model AI khác hoặc chụp lại ảnh rõ nét hơn.
                        </Text>
                    </Card>

                    <Card className="mb-6 p-1">
                        <select
                            value={selectedRetryModel}
                            onChange={(e) => setSelectedRetryModel(e.target.value)}
                            className="w-full bg-transparent border-none px-4 py-3 text-sm focus:ring-0"
                        >
                            {MODEL_LIST.map((model) => (
                                <option key={model.id} value={model.id}>{model.name}</option>
                            ))}
                        </select>
                    </Card>

                    <div className="space-y-3">
                        <Button onClick={handleRetry} disabled={isRetrying} className="w-full">
                            {isRetrying ? 'Đang thử lại...' : '🔄 Thử lại với model này'}
                        </Button>
                        <Button variant="ghost" onClick={() => setCurrentScreen(SCREENS.HOME)} className="w-full">
                            Quay lại trang chủ
                        </Button>
                    </div>
                </div>
            </Container>
        );
    }

    const currentInsight = TCM_INSIGHTS[insightIndex];

    return (
        <Container className="min-h-screen bg-theme-bg flex flex-col items-center justify-center p-6" safe>
            <div className="w-full max-w-md flex flex-col items-center">

                {/* 1. Header & Animation - PERFECTLY CENTERED */}
                <div className="relative w-48 h-48 mb-10 flex items-center justify-center">
                    {/* Pulsing AI Brain Background */}
                    <div className="absolute inset-0 bg-primary/5 rounded-full animate-pulse" />

                    {/* Rotating Rings */}
                    <div className="absolute inset-0 border-[3px] border-transparent border-t-primary border-b-primary rounded-full animate-spin duration-[3s]" />
                    <div className="absolute inset-3 border-[2px] border-transparent border-l-secondary border-r-secondary rounded-full animate-spin duration-[2s] opacity-70" style={{ animationDirection: 'reverse' }} />
                    <div className="absolute inset-8 border-[1px] border-transparent border-t-info rounded-full animate-spin duration-[1.5s] opacity-50" />

                    {/* Scanner Effect */}
                    <div className="absolute inset-0 flex items-center justify-center overflow-hidden rounded-full">
                        <div className="w-full h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent animate-scanner shadow-[0_0_15px_rgba(var(--color-primary),0.8)]" />
                    </div>

                    {/* Magnifier Icon */}
                    <div className="relative z-10 text-6xl drop-shadow-2xl">
                        🔍
                    </div>
                </div>

                {/* 2. Primary Status */}
                <div className="text-center mb-10">
                    <Text variant="h2" className="mb-3 animate-pulse">Đang phân tích...</Text>
                    <div className="h-6 flex items-center justify-center overflow-hidden">
                        <Text key={statusMessage} className="text-primary font-medium transition-all duration-500 animate-slide-up">
                            {statusMessage}
                        </Text>
                    </div>
                </div>

                {/* 3. Rotating Expert Insights - Apple Style Card */}
                <Card className="w-full mb-10 p-5 bg-gradient-to-br from-theme-card to-theme-secondary border-primary/10 shadow-xl overflow-hidden min-h-[120px] flex flex-col items-center justify-center text-center">
                    <div key={insightIndex} className="animate-fade-in space-y-2">
                        <Text className="text-xs font-bold uppercase tracking-widest text-primary block mb-1">
                            Kiến thức Y học Cổ truyền
                        </Text>
                        <Text className="font-semibold block text-theme-primary">
                            {currentInsight.title}
                        </Text>
                        <Text variant="caption" className="italic">
                            "{currentInsight.desc}"
                        </Text>
                    </div>
                </Card>

                {/* 4. Technical Progress Steps */}
                <div className="w-full space-y-4 px-4">
                    {TECHNICAL_STEPS.map((step, idx) => (
                        <div
                            key={step.id}
                            className={`flex items-center gap-4 transition-all duration-500 ${idx < currentStep ? 'opacity-100 text-success' :
                                    idx === currentStep ? 'opacity-100 scale-105' : 'opacity-20 translate-x-2'
                                }`}
                        >
                            <div className={`
                                w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all shadow-md
                                ${idx < currentStep ? 'bg-success text-white' :
                                    idx === currentStep ? 'bg-primary text-white shadow-primary/30 ring-4 ring-primary/10' :
                                        'bg-theme-tertiary text-theme-secondary'}
                            `}>
                                {idx < currentStep ? <Icons.Check /> : idx + 1}
                            </div>
                            <div className="flex flex-col">
                                <Text className={`font-semibold text-sm ${idx === currentStep ? 'text-primary' : ''}`}>
                                    {step.label}
                                </Text>
                                <Text variant="small" className="text-[10px]">
                                    {step.sub}
                                </Text>
                            </div>
                            {idx === currentStep && (
                                <div className="ml-auto w-12 h-1 bg-theme-tertiary rounded-full overflow-hidden">
                                    <div className="h-full bg-primary animate-progress-ind" />
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* 5. Confidence Indicator */}
                <div className="mt-12 text-center opacity-40">
                    <Text variant="small" className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                        Hệ thống phân tích bảo mật & chính xác
                    </Text>
                </div>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes scanner {
                    0% { transform: translateY(-100px); opacity: 0; }
                    50% { opacity: 1; }
                    100% { transform: translateY(100px); opacity: 0; }
                }
                .animate-scanner {
                    animation: scanner 3s infinite ease-in-out;
                }
                @keyframes progress-ind {
                    0% { width: 0%; transform: translateX(-100%); }
                    50% { width: 100%; transform: translateX(0); }
                    100% { width: 0%; transform: translateX(100%); }
                }
                .animate-progress-ind {
                    animation: progress-ind 2s infinite ease-in-out;
                }
                @keyframes slide-up {
                    from { transform: translateY(20px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                .animate-slide-up {
                    animation: slide-up 0.5s ease-out forwards;
                }
                @keyframes fade-in {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
                .animate-fade-in {
                    animation: fade-in 0.8s ease-out forwards;
                }
            `}} />
        </Container>
    );
}

export default AnalysisScreen;
